import axios, { type AxiosError, type AxiosResponse, type CancelTokenSource, type CancelToken } from 'axios'
import { ref, shallowRef } from 'vue'

import useAppLoading, { type LoadingMode } from '@/composables/common/loading/useAppLoading'

interface Options<T, TVariables> {
  onSuccess?: (data: T, response: AxiosResponse<T>, variables?: TVariables, page?: number | null) => void
  onError?: (error: AxiosError) => void
  immediate?: boolean
  variables?: TVariables
  loadingMode?: LoadingMode
  isPagination?: boolean
  itemPerPage?: number
}

const defaultObserverOptions = <T, TVariables>(options?: Options<T, TVariables>): Options<T, TVariables> => {
  const defaultOptions: Options<T, TVariables> = {
    immediate: false
  }

  if (options === undefined) {
    return defaultOptions
  }

  return {
    ...defaultOptions,
    ...options
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useAxiosResponse<T = unknown, TVariables = unknown> (
  request: (variables?: TVariables, cancelToken?: CancelToken, page?: number | null) => Promise<AxiosResponse<T>>,
  options?: Options<T, TVariables>
) {
  const appLoading = useAppLoading()
  const loadingId = ref('')
  const defaultOptions = defaultObserverOptions(options)
  const response = shallowRef<AxiosResponse<T>>()
  const data = shallowRef<T>()
  const isFinished = ref(false)
  const isLoading = ref(false)
  const isCanceled = ref(false)
  const error = shallowRef<AxiosError>()
  const cancelToken = shallowRef<CancelTokenSource>()
  const page = ref<number>(1)
  const nextPage = ref<number | null>(null)
  const loadMoreContentElm = ref<HTMLElement | null>(null)

  const cancel = (message?: string): void => {
    if (isFinished.value || !isLoading.value || cancelToken.value === undefined) return

    cancelToken.value.cancel(message)
    isCanceled.value = true
    isLoading.value = false
    isFinished.value = false
    removeDisplayLoading()
  }

  const execute = (variables?: TVariables): void => {
    addDisplayLoading()
    isFinished.value = false
    isLoading.value = true
    isCanceled.value = false
    error.value = undefined
    cancelToken.value = axios.CancelToken.source()
    request(variables, cancelToken.value.token, page.value)
      .then((r: AxiosResponse<T>) => {
        isLoading.value = false
        removeDisplayLoading()
        isFinished.value = true

        if (defaultOptions.onSuccess !== undefined) {
          defaultOptions.onSuccess(r.data, r, variables, page.value)
        }

        response.value = r
        data.value = r.data
      })
      .catch((e) => {
        if (import.meta.env.MODE === 'test' && ((typeof e === 'object' && !('response' in e)) || typeof e !== 'object')) {
          throw new Error(`[TestAxios] ${e}`)
        }

        isLoading.value = false
        removeDisplayLoading()
        isFinished.value = true
        error.value = e
        defaultOptions.onError !== undefined && error.value && defaultOptions.onError(error.value)
      })
  }

  const clear = (): void => {
    response.value = undefined
    data.value = undefined
    isCanceled.value = false
    isLoading.value = false
    isFinished.value = false
    error.value = undefined
    cancelToken.value?.cancel()
    cancelToken.value = undefined
    removeDisplayLoading()
  }

  const addDisplayLoading = (): void => {
    if (loadingId.value !== '') {
      removeDisplayLoading()
    }

    if (options?.loadingMode === undefined) {
      return
    }

    appLoading.addLoading(options.loadingMode, loadingId.value)
  }

  const removeDisplayLoading = (): void => {
    if (options?.loadingMode !== undefined) {
      appLoading.removeLoading(options.loadingMode, loadingId.value)
    }

    loadingId.value = ''
  }

  const loadMore = (): void => {
    if (data.value === undefined ||
      loadMoreContentElm.value === null ||
      nextPage.value === 1 ||
      nextPage.value === null) {
      return
    }

    if (loadMoreContentElm.value.scrollHeight - 80 <= loadMoreContentElm.value.scrollTop + loadMoreContentElm.value.offsetHeight && !isLoading.value) {
      page.value = nextPage.value
      execute()
    }
  }

  const setPage = (value: number): void => {
    page.value = value
    nextPage.value = null
  }

  if (defaultOptions.immediate) {
    execute(defaultOptions?.variables)
  }

  return {
    execute,
    response,
    data,
    error,
    isFinished,
    isLoading,
    cancel,
    isCanceled,
    clear,
    page,
    setPage,
    loadMoreContentElm,
    loadMore
  }
}
